import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { fmtSignal } from "@/lib/utils";
import { Search, Filter, ArrowUpRight, Sparkles, Loader2, Upload, RotateCcw, X, UserPlus, Plus, Users, Mail, Linkedin, ArrowDownUp, Globe, FileText, Archive, ArchiveRestore, ArrowUp, ArrowDown } from "lucide-react";

const STATUSES = ["all", "new", "contacted", "engaged", "replied", "meeting_booked"];

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const emptyContact = () => ({ full_name: "", email: "", _uid: uid() });

export default function ProspectsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("score");          // score | status | name | company
  const [direction, setDirection] = useState("desc"); // asc | desc
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState("");
  const [missingContact, setMissingContact] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [findingContacts, setFindingContacts] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState("");
  const [showReactivate, setShowReactivate] = useState(false);
  const fileRef = useRef(null);

  // Manual "Add prospects" modal
  const [showAdd, setShowAdd] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addMode, setAddMode] = useState("manual"); // manual | url
  const [urlInput, setUrlInput] = useState("");
  const [urlBusy, setUrlBusy] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [parsed, setParsed] = useState(null); // {contacts, count, no_email_count}
  // "From an RFQ" mode
  const [rfqBusy, setRfqBusy] = useState(false);
  const [rfqText, setRfqText] = useState("");
  const [rfqFileName, setRfqFileName] = useState("");
  const [rfqDraft, setRfqDraft] = useState(null);
  const rfqFileRef = useRef(null);
  // "From a file (CSV/Excel)" mode
  const [fileBusy, setFileBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const [filePreview, setFilePreview] = useState(null);
  const [includeNoEmail, setIncludeNoEmail] = useState(false);
  const importFileRef = useRef(null);
  // "From a mailbox" (M365 sent-items mining) mode
  const [mbAddress, setMbAddress] = useState("mateja@cargo-cfs.com");
  const [mbExclude, setMbExclude] = useState("europacific.com, epholding.org, cargo-cfs.com");
  const [mbBusy, setMbBusy] = useState(false);
  const [mbResult, setMbResult] = useState(null);
  const [mbError, setMbError] = useState("");
  const [addForm, setAddForm] = useState({
    category: "partnership", company: "", title: "", location: "", notes: "",
    contacts: [emptyContact(), emptyContact()],
  });

  const resetAdd = () => {
    setShowAdd(false); setAddMode("manual"); setUrlInput(""); setUrlError("");
    setParsed(null); setUrlBusy(false);
    setRfqText(""); setRfqFileName(""); setRfqDraft(null); setRfqBusy(false);
    setFileName(""); setFilePreview(null); setFileBusy(false); setIncludeNoEmail(false);
    setMbBusy(false); setMbResult(null); setMbError("");
    setAddForm({ category: "partnership", company: "", title: "", location: "", notes: "",
      contacts: [emptyContact(), emptyContact()] });
  };

  const readFileB64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const parseRfq = async (file) => {
    setRfqBusy(true); setRfqDraft(null);
    try {
      let payload;
      if (file) payload = { file_base64: await readFileB64(file), filename: file.name };
      else if (rfqText.trim()) payload = { text: rfqText };
      else { toast.error("Upload an RFQ file or paste the email text"); setRfqBusy(false); return; }
      const { data } = await api.post("/prospects/parse-rfq", payload);
      setRfqDraft({
        company: data.target_company || "", contact_name: data.contact_name || "",
        contact_email: data.contact_email || "", title: data.title || "",
        location: data.location || "", cargo_summary: data.cargo_summary || "",
        subject: data.subject || "", sender_company: data.sender_company || "",
        reasoning: data.reasoning || "",
      });
      setAddForm((f) => ({ ...f, category: ["partnership", "forwarder", "customer"].includes(data.suggested_category) ? data.suggested_category : "customer" }));
      toast.success("RFQ parsed — review the target below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not parse the RFQ");
    }
    setRfqBusy(false);
  };

  const onRfqFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) { setRfqFileName(file.name); await parseRfq(file); }
    e.target.value = "";
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFileName(file.name); setFileBusy(true); setFilePreview(null);
    try {
      const b64 = await readFileB64(file);
      const { data } = await api.post("/prospects/import-file", {
        filename: file.name, file_base64: b64, preview_only: true,
      });
      setFilePreview({ ...data, _b64: b64, _name: file.name });
      if (!data.with_email && !data.no_email_companies) toast.error("No contacts found in that file. Make sure it has 'Company' and 'Email' columns.");
      else if (!data.with_email) { setIncludeNoEmail(true); toast.warning(`No emails found — but ${data.no_email_companies} companies detected (add them for Apollo/LinkedIn enrichment)`); }
      else toast.success(`Found ${data.with_email} contact${data.with_email === 1 ? "" : "s"} with emails`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that file");
    }
    setFileBusy(false);
  };

  const submitFileImport = async () => {
    if (!filePreview?._b64) return;
    setAddSaving(true);
    try {
      const { data } = await api.post("/prospects/import-file", {
        filename: filePreview._name, file_base64: filePreview._b64,
        category: addForm.category, preview_only: false, include_no_email: includeNoEmail,
      });
      const added = data.created + (data.no_email_added || 0);
      toast.success(added ? `Added ${added} prospect${added === 1 ? "" : "s"}` : "Import complete", {
        description: `${data.segment.replace(/_/g, " ")}${data.updated ? ` · ${data.updated} enriched (mobile/phone filled in)` : ""}${data.no_email_added ? ` · ${data.no_email_added} company-only` : ""}${data.duplicates ? ` · ${data.duplicates} already existed` : ""}`,
      });
      resetAdd();
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not import contacts"); }
    setAddSaving(false);
  };

  const runMailboxParse = async () => {
    const mailbox = mbAddress.trim();
    if (!mailbox) { toast.error("Enter a mailbox address"); return; }
    setMbBusy(true); setMbError(""); setMbResult(null);
    try {
      const exclude = mbExclude.split(",").map((d) => d.trim().replace(/^@/, "")).filter(Boolean);
      const { data } = await api.post("/prospects/from-mailbox", {
        mailbox, category: addForm.category, exclude_domains: exclude, max_pages: 40,
      });
      setMbResult(data);
      toast.success(`Added ${data.created} prospect${data.created === 1 ? "" : "s"} from ${data.mailbox}`, {
        description: `${data.scanned_messages} sent messages scanned${data.duplicates ? ` · ${data.duplicates} already existed` : ""}${data.excluded_own_domain ? ` · ${data.excluded_own_domain} internal skipped` : ""}`,
      });
      await load();
    } catch (e) {
      setMbError(e?.response?.data?.detail || "Could not read that mailbox");
    }
    setMbBusy(false);
  };

  const setRfq = (k, v) => setRfqDraft((d) => ({ ...d, [k]: v }));

  const submitRfq = async () => {
    if (!rfqDraft?.company?.trim()) { toast.error("Target company is required"); return; }
    setAddSaving(true);
    try {
      const { data } = await api.post("/prospects/rfq-target", {
        company: rfqDraft.company, contact_name: rfqDraft.contact_name,
        contact_email: rfqDraft.contact_email, title: rfqDraft.title,
        location: rfqDraft.location, cargo_summary: rfqDraft.cargo_summary,
        subject: rfqDraft.subject, category: addForm.category,
      });
      if (data.duplicate) toast.info(`${rfqDraft.company} is already in your prospects`);
      else toast.success(`${rfqDraft.company} added as a target${data.has_email ? "" : " (no email yet — enrich via Apollo)"}`);
      resetAdd();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add target");
    }
    setAddSaving(false);
  };

  const startParse = async () => {
    if (!urlInput.trim()) { toast.error("Enter a website URL or company name"); return; }
    setUrlBusy(true); setUrlError(""); setParsed(null);
    try {
      const { data } = await api.post("/prospects/parse-url", { url: urlInput.trim() });
      const jobId = data.job_id;
      // poll up to ~6 min — large directories (100+ contacts) take a couple of minutes
      for (let i = 0; i < 130; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: s } = await api.get(`/prospects/parse-url/${jobId}`);
        if (s.status === "done") {
          setParsed({
            contacts: s.contacts || [], count: s.count || 0, no_email_count: s.no_email_count,
            website: s.website, linkedin_url: s.linkedin_url, emails: s.emails || [],
            pages_crawled: s.pages_crawled,
          });
          if (!s.contacts?.length && !s.linkedin_url) {
            setUrlError("No emails or LinkedIn found. Try the company's contact page URL, or use Lead Discovery → Apollo to find named people.");
          } else if (!s.contacts?.length) {
            toast.success(`Found the company's LinkedIn — no public emails on the site`);
          } else {
            toast.success(`Found ${s.count} contact${s.count === 1 ? "" : "s"}${s.pages_crawled > 1 ? ` across ${s.pages_crawled} pages` : ""}`);
          }
          break;
        }
        if (s.status === "error") { setUrlError(s.error || "Could not parse this page"); break; }
      }
    } catch (e) {
      setUrlError(e?.response?.data?.detail || "Could not parse this page");
    }
    setUrlBusy(false);
  };

  const submitBulk = async () => {
    if (!parsed?.contacts?.length) return;
    setAddSaving(true);
    try {
      const { data } = await api.post("/prospects/bulk-add", {
        category: addForm.category, contacts: parsed.contacts,
      });
      toast.success(`Added ${data.created} prospect${data.created === 1 ? "" : "s"}`, {
        description: `${data.segment.replace(/_/g, " ")}${data.duplicates ? ` · ${data.duplicates} already existed` : ""}`,
      });
      resetAdd();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add prospects"); }
    setAddSaving(false);
  };

  const setContact = (i, k, v) => setAddForm((f) => {
    const contacts = [...f.contacts];
    contacts[i] = { ...contacts[i], [k]: v };
    return { ...f, contacts };
  });
  const addContactRow = () => setAddForm((f) => ({ ...f, contacts: [...f.contacts, emptyContact()] }));
  const removeContactRow = (i) => setAddForm((f) => ({ ...f, contacts: f.contacts.filter((_, idx) => idx !== i) }));

  const submitAdd = async () => {
    if (!addForm.company.trim()) { toast.error("Company is required"); return; }
    const contacts = addForm.contacts.filter((c) => (c.email || "").trim());
    if (!contacts.length) { toast.error("Add at least one email"); return; }
    setAddSaving(true);
    try {
      const { data } = await api.post("/prospects/quick-add", { ...addForm, contacts });
      toast.success(`Added ${data.created} prospect${data.created === 1 ? "" : "s"}`, {
        description: `${data.segment.replace(/_/g, " ")}${data.duplicates ? ` · ${data.duplicates} duplicate(s) skipped` : ""}${data.invalid ? ` · ${data.invalid} invalid email(s)` : ""}`,
      });
      setShowAdd(false);
      setAddForm({ category: "partnership", company: "", title: "", location: "", notes: "",
        contacts: [emptyContact(), emptyContact()] });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add prospects"); }
    setAddSaving(false);
  };

  const PAGE = 1000;

  const load = async (offset = 0) => {
    const params = { limit: PAGE, offset, sort, direction };
    if (status !== "all") params.status = status;
    if (search) params.search = search;
    if (missingContact) params.missing_contact = true;
    if (showArchived) params.archived = true;
    const { data } = await api.get("/prospects", { params });
    const next = data.prospects || [];
    setItems(offset === 0 ? next : (prev) => [...prev, ...next]);
    setTotal(data.total || 0);
    setHasMore(!!data.has_more);
    if (offset === 0) setSelected([]);
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try { await load(items.length); } finally { setLoadingMore(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, sort, direction, missingContact, showArchived]);

  const setSortCol = (id) => {
    if (sort === id) setDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(id); setDirection(id === "score" ? "desc" : "asc"); }
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = items.length > 0 && selected.length === items.length;
  const toggleSelectAll = () => setSelected(allSelected ? [] : items.map((p) => p.id));

  const bulkArchive = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Archive ${selected.length} prospect${selected.length === 1 ? "" : "s"}? They'll be hidden and blocked from ALL outreach (email, LinkedIn, SMS, DMs).`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/prospects/${selected[0]}/archive`, { reason: "bulk archive — not a fit", ids: selected });
      const n = data.archived ?? selected.length;
      toast.success(`Archived ${n} prospect${n === 1 ? "" : "s"} — excluded from all outreach`);
      const ids = new Set(selected);
      setItems((l) => l.filter((x) => !ids.has(x.id)));
      setTotal((t) => Math.max(0, t - n));
      setSelected([]);
    } catch { toast.error("Could not bulk archive"); }
    setBulkBusy(false);
  };

  const bulkMarkCustomer = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Mark ${selected.length} prospect${selected.length === 1 ? "" : "s"} as active customer${selected.length === 1 ? "" : "s"}? Prospecting stops for them, but the weekly warehouse email & SMS follow-up continue.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/prospects/${selected[0]}/mark-customer`, { ids: selected });
      const n = data.marked ?? selected.length;
      toast.success(`Marked ${n} as active customer${n === 1 ? "" : "s"} — prospecting stopped, operational emails/SMS continue`);
      const ids = new Set(selected);
      setItems((l) => l.map((x) => (ids.has(x.id) ? { ...x, customer_active: true, status: "customer" } : x)));
      setSelected([]);
    } catch { toast.error("Could not mark as customer"); }
    setBulkBusy(false);
  };

  const bulkRestore = async () => {
    if (!selected.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/prospects/${selected[0]}/unarchive`, { ids: selected });
      const n = data.restored ?? selected.length;
      toast.success(`Restored ${n} prospect${n === 1 ? "" : "s"} to active prospecting`);
      const ids = new Set(selected);
      setItems((l) => l.filter((x) => !ids.has(x.id)));
      setTotal((t) => Math.max(0, t - n));
      setSelected([]);
    } catch { toast.error("Could not bulk restore"); }
    setBulkBusy(false);
  };

  const archive = async (e, p) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Archive ${p.full_name || p.company || "this prospect"}? They'll be hidden and blocked from ALL outreach (email, LinkedIn, SMS, DMs).`)) return;
    setRowBusy(p.id);
    try {
      await api.post(`/prospects/${p.id}/archive`, { reason: "not a fit" });
      toast.success("Archived — excluded from all outreach");
      setItems((l) => l.filter((x) => x.id !== p.id)); setTotal((t) => Math.max(0, t - 1));
    } catch { toast.error("Could not archive"); }
    setRowBusy("");
  };
  const unarchive = async (e, p) => {
    e.preventDefault(); e.stopPropagation();
    setRowBusy(p.id);
    try {
      await api.post(`/prospects/${p.id}/unarchive`);
      toast.success("Restored to active prospecting");
      setItems((l) => l.filter((x) => x.id !== p.id)); setTotal((t) => Math.max(0, t - 1));
    } catch { toast.error("Could not restore"); }
    setRowBusy("");
  };
  const markCustomer = async (e, p) => {
    e.preventDefault(); e.stopPropagation();
    setRowBusy(p.id);
    try {
      await api.post(`/prospects/${p.id}/mark-customer`);
      toast.success("Marked as active customer — prospecting stopped, weekly warehouse email & SMS continue");
      setItems((l) => l.map((x) => (x.id === p.id ? { ...x, customer_active: true, status: "customer" } : x)));
    } catch { toast.error("Could not mark as customer"); }
    setRowBusy("");
  };
  const unmarkCustomer = async (e, p) => {
    e.preventDefault(); e.stopPropagation();
    setRowBusy(p.id);
    try {
      await api.post(`/prospects/${p.id}/unmark-customer`);
      toast.success("Back to active prospecting");
      setItems((l) => l.map((x) => (x.id === p.id ? { ...x, customer_active: false, status: "engaged" } : x)));
    } catch { toast.error("Could not update"); }
    setRowBusy("");
  };

  const handleSearch = (e) => { e.preventDefault(); load(); };

  const enrichHighFit = async () => {
    setEnriching(true);
    try {
      const { data } = await api.post("/apollo/enrich-icp", { min_score: 85, limit: 50, resolve_linkedin: true });
      if (data.status === "noop" || !data.job_id) {
        toast.info("Nothing to enrich", { description: "No un-enriched ICP prospects scoring 85+." });
        setEnriching(false);
        return;
      }
      toast.info(`Enriching ${data.total} high-fit prospects via Apollo…`, { description: "Verified emails + LinkedIn. ~1 credit each." });
      const poll = setInterval(async () => {
        try {
          const { data: job } = await api.get(`/apollo/enrich-icp/${data.job_id}`);
          if (job.status === "done") {
            clearInterval(poll);
            setEnriching(false);
            const r = job.result || {};
            toast.success("Apollo enrichment complete", {
              description: `${r.verified_emails || 0} verified emails · ${r.linkedin_resolved || 0} LinkedIn profiles · ${r.no_match || 0} no match (of ${r.considered || 0}).`,
            });
            load();
          } else if (job.status === "failed") {
            clearInterval(poll);
            setEnriching(false);
            toast.error("Enrichment failed", { description: job.error || "Unknown error" });
          }
        } catch (e) { /* keep polling */ }
      }, 3000);
    } catch (e) {
      setEnriching(false);
      const msg = e?.response?.data?.detail || "Could not start enrichment.";
      toast.error("Enrichment error", { description: msg });
    }
  };

  const findCompanyContacts = async () => {
    setFindingContacts(true);
    try {
      const { data } = await api.post("/apollo/find-company-contacts", {
        per_company: 5, max_total: 100, only_without_contacts: true,
      });
      if (!data.job_id) {
        toast.info("Nothing to do", { description: data.message || "No target companies found." });
        setFindingContacts(false);
        return;
      }
      toast.info("Finding real contacts at your companies…", {
        description: "Apollo: up to 5 decision-makers per company, verified email + LinkedIn. Up to ~100 credits.",
      });
      const poll = setInterval(async () => {
        try {
          const { data: job } = await api.get(`/apollo/find-company-contacts/${data.job_id}`);
          if (job.status === "done") {
            clearInterval(poll);
            setFindingContacts(false);
            const r = job.result || {};
            if ((r.found || 0) === 0) {
              toast.info("No new contacts found", { description: r.message || "Check Apollo credits/plan." });
            } else {
              toast.success("Found new contacts", {
                description: `${r.created || 0} new prospects · ${r.emails || 0} with email · ${r.linkedin_resolved || 0} LinkedIn · across ${r.companies_with_hits || 0} companies.`,
              });
            }
            load();
          } else if (job.status === "failed") {
            clearInterval(poll);
            setFindingContacts(false);
            toast.error("Contact search failed", { description: job.error || "Unknown error" });
          }
        } catch (e) { /* keep polling */ }
      }, 4000);
    } catch (e) {
      setFindingContacts(false);
      toast.error("Could not start contact search", {
        description: e?.response?.data?.detail || "Apollo may not be configured.",
      });
    }
  };
  // note-by-note (never loading the whole thing into one string — a 500MB+ .enex would
  // otherwise blow past Chrome's max string length) and keep only the card text, dropping
  // embedded base64 images. So we upload a few MB of JSON instead of hundreds of MB.
  const parseEnex = async (file, onProgress) => {
    const decoder = document.createElement("textarea");
    const decode = (s) => { decoder.innerHTML = s; return decoder.value; };
    const cards = [];

    const extractCard = (noteXml) => {
      // ignore everything before the opening <note> (file header / prev remainder noise)
      const start = noteXml.lastIndexOf("<note>");
      const xml = start !== -1 ? noteXml.slice(start) : noteXml;
      const tM = xml.match(/<title>([\s\S]*?)<\/title>/i);
      const cM = xml.match(/<content>([\s\S]*?)<\/content>/i);
      let body = cM ? cM[1] : "";
      body = body.replace(/<!\[CDATA\[/g, "").replace(/\]\]>/g, "");
      body = body.replace(/<[^>]+>/g, "\n");
      body = decode(body).replace(/[ \t]+/g, " ");
      const text = body.split("\n").map((s) => s.trim()).filter(Boolean).join("\n").slice(0, 1400);
      const title = tM ? decode(tM[1].replace(/<[^>]+>/g, "")).trim().slice(0, 150) : "";
      if (text || title) cards.push({ title, text });
    };

    // Streaming read. file.stream() + TextDecoderStream are supported in modern Chromium.
    const stream = file.stream().pipeThrough(new TextDecoderStream("utf-8"));
    const reader = stream.getReader();
    let buf = "";
    let readBytes = 0;
    const MAX_BUF = 64 * 1024 * 1024; // 64MB safety valve for a pathologically large single note
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += value;
      readBytes += value.length;
      let idx;
      while ((idx = buf.indexOf("</note>")) !== -1) {
        extractCard(buf.slice(0, idx));
        buf = buf.slice(idx + 7);
      }
      // A single note carrying a giant base64 image: strip resources, then hard-trim.
      if (buf.length > MAX_BUF) {
        buf = buf.replace(/<resource>[\s\S]*?<\/resource>/gi, "");
        if (buf.length > MAX_BUF) buf = buf.slice(-1024 * 1024);
      }
      if (onProgress) onProgress(cards.length, readBytes);
    }
    if (buf.indexOf("<note>") !== -1) extractCard(buf);
    return cards;
  };

  const importEnex = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (!files.length) return;
    setImporting(true);
    try {
      // Parse all files client-side first (streamed — safe for very large .enex)
      let allCards = [];
      for (const file of files) {
        const cards = await parseEnex(file, (found, bytes) => {
          if (found && found % 250 === 0) {
            toast.info(`Reading ${file.name}… ${found} cards so far (${Math.round(bytes / 1048576)} MB)`);
          }
        });
        allCards = allCards.concat(cards);
      }
      if (!allCards.length) { toast.error("Nothing found", { description: "No business cards detected in the file(s)." }); setImporting(false); return; }

      const { data: init } = await api.post("/evernote/import/init-cards", { filename: files[0].name });
      const jobId = init.job_id;
      const BATCH = 200;
      const totalBatches = Math.ceil(allCards.length / BATCH);
      for (let i = 0; i < totalBatches; i++) {
        const slice = allCards.slice(i * BATCH, (i + 1) * BATCH);
        await api.post("/evernote/import/add-cards", { job_id: jobId, seq: i, cards: slice });
        toast.info(`Uploading cards… ${Math.min((i + 1) * BATCH, allCards.length)}/${allCards.length}`);
      }
      await api.post("/evernote/import/run", { job_id: jobId });
      setImportStatus(`Extracting 0/${allCards.length}…`);
      toast.info(`Extracting contacts from ${allCards.length} cards…`, { description: "Verified emails + segmentation. Large notebooks take a few minutes." });
      const poll = setInterval(async () => {
        try {
          const { data: job } = await api.get(`/evernote/import/${jobId}`);
          const r = job.result || {};
          const total = job.total_cards || allCards.length;
          const processed = r.processed || 0;
          if (job.status === "done") {
            clearInterval(poll); setImporting(false); setImportStatus("");
            toast.success("Business cards imported", {
              description: `${r.created_email || 0} contactable + ${r.created_no_email || 0} no-email · ${r.skipped_dup || 0} duplicates skipped (of ${r.notes || 0} cards).`,
            });
            load();
          } else if (job.status === "failed") {
            clearInterval(poll); setImporting(false); setImportStatus("");
            toast.error("Import failed", { description: job.error || "Unknown error" });
          } else if (job.status === "interrupted") {
            // Backend restarted mid-job; it auto-resumes on the next server start.
            setImportStatus(`Paused at ${processed}/${total} — resuming…`);
          } else {
            setImportStatus(`Extracting ${processed}/${total}…`);
          }
        } catch (e) { /* keep polling */ }
      }, 5000);
    } catch (e) {
      setImporting(false); setImportStatus("");
      toast.error("Import error", { description: e?.response?.data?.detail || "Could not read or upload the .enex file(s)." });
    }
  };

  return (
    <DashboardLayout
      eyebrow="Prospects"
      title="Lead intelligence"
      actions={
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowAdd(true)} size="sm" data-testid="add-prospect-btn">
            <UserPlus className="w-3.5 h-3.5" />
            <span className="ml-1.5">Add prospects</span>
          </Button>
          <input ref={fileRef} type="file" accept=".enex" multiple onChange={importEnex} className="hidden" data-testid="enex-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={importing} size="sm" variant="outline" data-testid="import-enex-btn"
            className="hidden md:inline-flex">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            <span className="ml-1.5" data-testid="import-enex-status">{importing ? (importStatus || "Importing…") : "Import cards (Evernote)"}</span>
          </Button>
          <Button onClick={() => setShowReactivate(true)} size="sm" variant="outline" data-testid="reactivate-lane-btn"
            className="hidden md:inline-flex">
            <RotateCcw className="w-3.5 h-3.5" />
            <span className="ml-1.5">Reactivate by lane</span>
          </Button>
          <Button onClick={findCompanyContacts} disabled={findingContacts} size="sm" data-testid="find-company-contacts-btn"
            className="hidden md:inline-flex">
            {findingContacts ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
            <span className="ml-1.5">{findingContacts ? "Finding…" : "Find real contacts"}</span>
          </Button>
          <Button onClick={enrichHighFit} disabled={enriching} size="sm" data-testid="enrich-highfit-btn"
            className="hidden md:inline-flex">
            {enriching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            <span className="ml-1.5">{enriching ? "Enriching…" : "Enrich high-fit (Apollo)"}</span>
          </Button>
          <form onSubmit={handleSearch} className="hidden md:flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input data-testid="prospects-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, company..." className="pl-8 h-9 w-64" />
            </div>
            <Button type="submit" variant="outline" size="sm" data-testid="prospects-search-btn"><Filter className="w-3.5 h-3.5" /></Button>
          </form>
        </div>
      }
    >
      {/* Status filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`prospects-filter-${s}`}
            className={`px-3 py-1 rounded-sm text-xs border transition-colors ${
              status === s ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
            }`}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground font-mono" data-testid="prospects-total-count">
          {items.length < total ? `${items.length} of ${total}` : total} prospects
        </span>
      </div>

      {/* Quick view + sort */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <button onClick={() => setMissingContact((v) => !v)} data-testid="prospects-missing-contact-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs border transition-colors ${
            missingContact ? "border-amber-500 bg-amber-500/15 text-amber-400" : "border-border text-muted-foreground hover:text-foreground"
          }`}>
          <X className="w-3 h-3" /> No email & no LinkedIn
        </button>
        {missingContact && (
          <span className="text-[0.7rem] text-muted-foreground">
            Unreachable contacts — use <b>Find real contacts</b> to enrich them.
          </span>
        )}
        <button onClick={() => setShowArchived((v) => !v)} data-testid="prospects-archived-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs border transition-colors ${
            showArchived ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
          }`}>
          <Archive className="w-3 h-3" /> {showArchived ? "Viewing archived" : "Archived"}
        </button>
        <div className="ml-auto inline-flex items-center gap-1.5">
          <ArrowDownUp className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-[0.7rem] text-muted-foreground uppercase tracking-wider">Sort</span>
          {[
            { id: "score", label: "Score" },
            { id: "company", label: "Company" },
            { id: "status", label: "Status" },
            { id: "name", label: "Name" },
          ].map((o) => (
            <button key={o.id} onClick={() => setSortCol(o.id)} data-testid={`prospects-sort-${o.id}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-xs border transition-colors ${
                sort === o.id ? "border-signal-500 bg-signal-500/15 text-signal-500" : "border-border text-muted-foreground hover:text-foreground"
              }`}>
              {o.label}
              {sort === o.id && (direction === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-sm overflow-hidden">
        {selected.length > 0 && (
          <div className="flex items-center gap-3 px-5 py-2.5 bg-signal-500/10 border-b border-signal-500/30" data-testid="prospects-bulk-bar">
            <span className="text-xs font-medium" data-testid="prospects-selected-count">{selected.length} selected</span>
            {showArchived ? (
              <Button size="sm" className="h-7 rounded-sm" onClick={bulkRestore} disabled={bulkBusy} data-testid="prospects-bulk-restore-btn">
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" />}
                {bulkBusy ? "Restoring…" : `Bulk restore (${selected.length})`}
              </Button>
            ) : (
              <>
                <Button size="sm" className="h-7 rounded-sm bg-emerald-600 hover:bg-emerald-500" onClick={bulkMarkCustomer} disabled={bulkBusy} data-testid="prospects-bulk-customer-btn">
                  {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 mr-1.5" />}
                  {bulkBusy ? "Working…" : `Mark customer (${selected.length})`}
                </Button>
                <Button size="sm" variant="destructive" className="h-7 rounded-sm" onClick={bulkArchive} disabled={bulkBusy} data-testid="prospects-bulk-archive-btn">
                  {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Archive className="w-3.5 h-3.5 mr-1.5" />}
                  {bulkBusy ? "Archiving…" : `Bulk archive (${selected.length})`}
                </Button>
              </>
            )}
            <button onClick={() => setSelected([])} className="text-xs text-muted-foreground hover:text-foreground ml-auto" data-testid="prospects-clear-selection">Clear</button>
          </div>
        )}
        <div className="grid grid-cols-12 px-5 py-3 bg-muted/40 text-[0.7rem] uppercase tracking-wider text-muted-foreground font-semibold select-none">
          <div className="col-span-4 flex items-center gap-2">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="accent-signal-500 cursor-pointer" title="Select all on this page" data-testid="prospects-select-all" />
            <button onClick={() => setSortCol("name")} className="text-left inline-flex items-center gap-1 hover:text-foreground" data-testid="prospects-col-name">Prospect {sort === "name" && (direction === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}</button>
          </div>
          <button onClick={() => setSortCol("company")} className="col-span-3 text-left inline-flex items-center gap-1 hover:text-foreground" data-testid="prospects-col-company">Company {sort === "company" && (direction === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}</button>
          <div className="col-span-2">Signals</div>
          <div className="col-span-1 text-center">Contact</div>
          <button onClick={() => setSortCol("score")} className="col-span-1 inline-flex items-center justify-end gap-1 hover:text-foreground" data-testid="prospects-col-score">Score {sort === "score" && (direction === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}</button>
          <button onClick={() => setSortCol("status")} className="col-span-1 inline-flex items-center justify-end gap-1 hover:text-foreground" data-testid="prospects-col-status">Status {sort === "status" && (direction === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}</button>
        </div>
        {items.length === 0 && <div className="p-10 text-sm text-center text-muted-foreground">No prospects match.</div>}
        {items.map((p, i) => {
          const hasEmail = !!(p.email && p.email.trim());
          const hasLi = !!((p.linkedin_url && p.linkedin_url.trim()) || (p.linkedin_provider_id && p.linkedin_provider_id.trim()));
          return (
          <Link to={`/app/prospects/${p.id}`} key={p.id} data-testid={`prospect-row-${i}`}
            className="relative group grid grid-cols-12 px-5 py-4 border-t border-border hover:bg-muted/30 transition-colors items-center">
            <div className="col-span-4 min-w-0 flex items-center gap-2">
              <input type="checkbox" checked={selected.includes(p.id)} readOnly
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelect(p.id); }}
                className="accent-signal-500 cursor-pointer shrink-0" data-testid={`prospect-select-${i}`} />
              <div className="min-w-0">
                <div className="font-medium truncate flex items-center gap-2">
                  {p.full_name}
                  {p.customer_active && <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid={`prospect-customer-badge-${i}`}>Customer</span>}
                  <ArrowUpRight className="w-3 h-3 text-muted-foreground" />
                </div>
                <div className="text-xs text-muted-foreground truncate">{p.title}</div>
              </div>
            </div>
            <div className="col-span-3 min-w-0">
              <div className="text-sm truncate">{p.company}</div>
              <div className="text-[0.65rem] text-muted-foreground truncate uppercase tracking-wider">{p.industry}</div>
            </div>
            <div className="col-span-2 flex flex-wrap gap-1">
              {(p.signals || []).slice(0, 1).map((s) => (
                <span key={s} className="signal-pill">{fmtSignal(s)}</span>
              ))}
            </div>
            <div className="col-span-1 flex items-center justify-center gap-1.5" data-testid={`prospect-contact-${i}`}>
              <Mail className={`w-3.5 h-3.5 ${hasEmail ? "text-emerald-400" : "text-muted-foreground/30"}`} aria-label={hasEmail ? "Has email" : "No email"} />
              <Linkedin className={`w-3.5 h-3.5 ${hasLi ? "text-sky-400" : "text-muted-foreground/30"}`} aria-label={hasLi ? "Has LinkedIn" : "No LinkedIn"} />
            </div>
            <div className="col-span-1 text-right font-mono text-base font-semibold text-signal-500">{p.score}</div>
            <div className="col-span-1 text-right text-[0.65rem] uppercase tracking-wider text-muted-foreground">{p.status?.replace(/_/g, " ")}</div>

            {/* Hover actions */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-background/95 border border-border rounded-sm px-1 py-0.5 shadow-sm">
              {showArchived ? (
                <button onClick={(e) => unarchive(e, p)} disabled={rowBusy === p.id} title="Restore to prospecting" data-testid={`prospect-unarchive-${i}`}
                  className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground">
                  {rowBusy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArchiveRestore className="w-3.5 h-3.5" />}
                </button>
              ) : (
                <>
                  {p.customer_active ? (
                    <button onClick={(e) => unmarkCustomer(e, p)} disabled={rowBusy === p.id} title="Back to prospecting" data-testid={`prospect-unmark-customer-${i}`}
                      className="px-2 py-1 rounded-sm hover:bg-muted text-[0.62rem] uppercase tracking-wider text-emerald-400">
                      Customer ✓
                    </button>
                  ) : (
                    <button onClick={(e) => markCustomer(e, p)} disabled={rowBusy === p.id} title="Mark as active customer (stops prospecting, keeps weekly warehouse email + SMS)" data-testid={`prospect-mark-customer-${i}`}
                      className="px-2 py-1 rounded-sm hover:bg-emerald-500/15 text-[0.62rem] uppercase tracking-wider text-muted-foreground hover:text-emerald-400">
                      Mark customer
                    </button>
                  )}
                  <button onClick={(e) => archive(e, p)} disabled={rowBusy === p.id} title="Archive (not a fit — blocks all outreach)" data-testid={`prospect-archive-${i}`}
                    className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-red-400">
                    {rowBusy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                  </button>
                </>
              )}
            </div>
          </Link>
          );
        })}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-5">
          <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}
            data-testid="prospects-load-more-btn">
            {loadingMore ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Loading…</> : `Load more (${total - items.length} remaining)`}
          </Button>
        </div>
      )}

      {showReactivate && (
        <ReactivateByLane onClose={() => setShowReactivate(false)} onDone={() => load()} />
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto"
          onClick={() => !addSaving && setShowAdd(false)} data-testid="add-prospect-modal">
          <div className="bg-card border border-border rounded-sm shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-signal-500" />
                <div>
                  <div className="label-eyebrow">Add prospects</div>
                  <div className="font-display font-semibold">Add contacts &amp; choose their category</div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowAdd(false)} data-testid="add-prospect-close"><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-5 gap-2" data-testid="add-mode-toggle">
                {[
                  { id: "manual", label: "Enter manually" },
                  { id: "url", label: "From a website" },
                  { id: "file", label: "From a file" },
                  { id: "mailbox", label: "From a mailbox" },
                  { id: "rfq", label: "From an RFQ" },
                ].map((m) => (
                  <button key={m.id} type="button" onClick={() => setAddMode(m.id)}
                    data-testid={`add-mode-${m.id}`}
                    className={`px-2.5 py-2 rounded-sm border text-xs font-medium transition-colors ${
                      addMode === m.id ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
                    }`}>{m.label}</button>
                ))}
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Category</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { id: "partnership", label: "Partnership prospect", blurb: "Be their local partner / handling agent (e.g. forwarders like Quehenberger)" },
                    { id: "forwarder", label: "Forwarder", blurb: "Pitch the North Adriatic routing advantage (Koper/Rijeka)" },
                    { id: "customer", label: "Direct customer / shipper", blurb: "Europacific as their logistics provider into CEE" },
                  ].map((c) => (
                    <button key={c.id} type="button" onClick={() => setAddForm((f) => ({ ...f, category: c.id }))}
                      data-testid={`add-category-${c.id}`}
                      className={`text-left px-3 py-2 rounded-sm border transition-colors ${
                        addForm.category === c.id ? "border-signal-500 bg-signal-500/10" : "border-border hover:border-foreground/40"
                      }`}>
                      <div className="text-sm font-medium">{c.label}</div>
                      <div className="text-[0.7rem] text-muted-foreground">{c.blurb}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3" style={{ display: addMode === "manual" ? undefined : "none" }}>
                <div>
                  <label className="label-eyebrow block mb-1.5">Company <span className="text-destructive">*</span></label>
                  <Input data-testid="add-company" value={addForm.company} onChange={(e) => setAddForm((f) => ({ ...f, company: e.target.value }))} placeholder="Quehenberger" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1.5">Title <span className="text-muted-foreground">(optional)</span></label>
                  <Input data-testid="add-title" value={addForm.title} onChange={(e) => setAddForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Branch Manager" />
                </div>
                <div className="col-span-2">
                  <label className="label-eyebrow block mb-1.5">Location <span className="text-muted-foreground">(optional)</span></label>
                  <Input data-testid="add-location" value={addForm.location} onChange={(e) => setAddForm((f) => ({ ...f, location: e.target.value }))} placeholder="e.g. Salzburg, Austria" />
                </div>
              </div>
              <div style={{ display: addMode === "manual" ? undefined : "none" }}>
                <label className="label-eyebrow block mb-1.5">Contacts</label>
                <div className="space-y-2">
                  {addForm.contacts.map((c, i) => (
                    <div key={c._uid || i} className="flex items-center gap-2" data-testid={`add-contact-row-${i}`}>
                      <Input className="w-40" placeholder="Full name (optional)" value={c.full_name}
                        onChange={(e) => setContact(i, "full_name", e.target.value)} data-testid={`add-contact-name-${i}`} />
                      <Input className="flex-1" placeholder="email@company.com" value={c.email}
                        onChange={(e) => setContact(i, "email", e.target.value)} data-testid={`add-contact-email-${i}`} />
                      {addForm.contacts.length > 1 && (
                        <button onClick={() => removeContactRow(i)} className="text-muted-foreground hover:text-destructive" data-testid={`add-contact-remove-${i}`}>
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addContactRow} className="mt-2 rounded-sm" data-testid="add-contact-row-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add another contact
                </Button>
              </div>
              <div style={{ display: addMode === "manual" ? undefined : "none" }}>
                <label className="label-eyebrow block mb-1.5">Notes <span className="text-muted-foreground">(optional)</span></label>
                <Input data-testid="add-notes" value={addForm.notes} onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Context for the AI (e.g. met at Munich expo)" />
              </div>

              {addMode === "url" && (
                <div className="space-y-3" data-testid="add-url-section">
                  <div>
                    <label className="label-eyebrow block mb-1.5">Website URL or company name</label>
                    <div className="flex gap-2">
                      <Input data-testid="add-url-input" value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        placeholder="europacific.si  ·  or  ·  Hunicorn Shipping" />
                      <Button onClick={startParse} disabled={urlBusy} className="rounded-sm shrink-0" data-testid="add-url-fetch">
                        {urlBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                        {urlBusy ? "Scanning…" : "Find contacts"}
                      </Button>
                    </div>
                    <p className="text-[0.7rem] text-muted-foreground mt-1.5">
                      Enter a <strong>website</strong> (we crawl its contact/about/team pages for emails + LinkedIn) — or just a <strong>company name</strong> and we'll find its site & LinkedIn for you. Also works for directory/listing pages. Takes ~10–60s.
                    </p>
                  </div>
                  {urlBusy && (
                    <div className="rounded-sm border border-border bg-muted/30 px-3 py-3 text-xs text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching, crawling pages and extracting contacts…
                    </div>
                  )}
                  {urlError && (
                    <div className="rounded-sm border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="add-url-error">{urlError}</div>
                  )}
                  {parsed && (
                    <div data-testid="add-url-results" className="space-y-2">
                      {(parsed.website || parsed.linkedin_url) && (
                        <div className="rounded-sm border border-signal-500/30 bg-signal-500/5 px-3 py-2 text-xs flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="add-url-discovered">
                          {parsed.website && <a href={parsed.website} target="_blank" rel="noreferrer" className="text-signal-500 hover:underline inline-flex items-center gap-1"><Globe className="w-3 h-3" /> {parsed.website.replace(/^https?:\/\//, "")}</a>}
                          {parsed.linkedin_url && <a href={parsed.linkedin_url} target="_blank" rel="noreferrer" className="text-signal-500 hover:underline inline-flex items-center gap-1"><Linkedin className="w-3 h-3" /> Company page</a>}
                          {parsed.pages_crawled > 0 && <span className="text-muted-foreground">{parsed.pages_crawled} page{parsed.pages_crawled === 1 ? "" : "s"} crawled</span>}
                        </div>
                      )}
                      {parsed.count > 0 ? (
                        <>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium">{parsed.count} contact{parsed.count === 1 ? "" : "s"} with emails</span>
                            {parsed.no_email_count ? <span className="text-[0.7rem] text-muted-foreground">{parsed.no_email_count} skipped (no email)</span> : null}
                          </div>
                          <div className="max-h-56 overflow-y-auto rounded-sm border border-border divide-y divide-border">
                            {parsed.contacts.map((c, i) => (
                              <div key={c.email || i} className="px-3 py-2 text-xs flex items-center justify-between gap-3" data-testid={`add-url-row-${i}`}>
                                <span className="font-medium truncate">{c.company || c.contact_name || "—"}</span>
                                <span className="text-muted-foreground truncate">{c.email}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-[0.7rem] text-muted-foreground mt-2">These will be added under the <strong>{addForm.category}</strong> category you selected above.</p>
                        </>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          No public emails on the site, but we found the company above. Tip: use <strong>Lead Discovery → Apollo</strong> (or the "Find contacts" button on Page Visitors) to get named decision-makers there.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {addMode === "file" && (
                <div className="space-y-3" data-testid="add-file-section">
                  <p className="text-[0.75rem] text-muted-foreground">
                    Upload a <strong>CSV or Excel</strong> export of a directory/member list. We auto-detect the
                    columns (company, email, phone, website, location) and add every row that has an email —
                    the reliable way to import large lists (e.g. a port-community directory) in one go.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input ref={importFileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onImportFile} className="hidden" data-testid="import-file-input" />
                    <Button variant="outline" size="sm" className="rounded-sm" disabled={fileBusy}
                      onClick={() => importFileRef.current?.click()} data-testid="import-file-btn">
                      {fileBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                      {fileBusy ? "Reading file…" : "Choose CSV / Excel file"}
                    </Button>
                    {fileName && <span className="text-[0.7rem] text-muted-foreground inline-flex items-center gap-1"><FileText className="w-3 h-3" />{fileName}</span>}
                  </div>
                  {filePreview && (
                    <div data-testid="import-file-results" className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{filePreview.with_email} contact{filePreview.with_email === 1 ? "" : "s"} with emails</span>
                        <span className="text-[0.7rem] text-muted-foreground">{filePreview.total_rows} row{filePreview.total_rows === 1 ? "" : "s"} in file</span>
                      </div>
                      {filePreview.with_email > 0 && (
                        <div className="max-h-56 overflow-y-auto rounded-sm border border-border divide-y divide-border">
                          {filePreview.sample.map((c, i) => (
                            <div key={c.email || i} className="px-3 py-2 text-xs flex items-center justify-between gap-3" data-testid={`import-file-row-${i}`}>
                              <span className="font-medium truncate">{c.company || c.contact_name || "—"}</span>
                              <span className="text-muted-foreground truncate">{c.email}</span>
                            </div>
                          ))}
                          {filePreview.with_email > filePreview.sample.length && (
                            <div className="px-3 py-2 text-[0.7rem] text-muted-foreground">…and {filePreview.with_email - filePreview.sample.length} more</div>
                          )}
                        </div>
                      )}
                      {filePreview.no_email_companies > 0 && (
                        <div className="rounded-sm border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 space-y-2" data-testid="import-file-noemail">
                          <div className="text-xs">
                            <strong>{filePreview.no_email_companies}</strong> compan{filePreview.no_email_companies === 1 ? "y" : "ies"} in this file have <strong>no email</strong> (e.g. directory hid it). You can still add them as company-only prospects and enrich contacts later via <strong>Apollo / Find real contacts</strong>.
                          </div>
                          <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="import-file-noemail-toggle">
                            <input type="checkbox" checked={includeNoEmail} onChange={(e) => setIncludeNoEmail(e.target.checked)} className="accent-signal-500" />
                            Also add the {filePreview.no_email_companies} companies without emails
                          </label>
                        </div>
                      )}
                      {(filePreview.with_email > 0 || (includeNoEmail && filePreview.no_email_companies > 0)) && (
                        <p className="text-[0.7rem] text-muted-foreground">Added under the <strong>{addForm.category}</strong> category selected above. Duplicates are skipped automatically.</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {addMode === "mailbox" && (
                <div className="space-y-3" data-testid="add-mailbox-section">
                  <p className="text-[0.75rem] text-muted-foreground">
                    Mine a colleague's <strong>Sent</strong> folder (Microsoft 365) for the people they've emailed,
                    drop your own company domains, and add the rest as prospects. Reads the mailbox app-only —
                    it must belong to your connected M365 tenant.
                  </p>
                  <div>
                    <label className="label-eyebrow block mb-1.5">Mailbox address</label>
                    <Input data-testid="mailbox-address" value={mbAddress}
                      onChange={(e) => setMbAddress(e.target.value)} placeholder="person@yourcompany.com" />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1.5">Exclude these domains (your own companies)</label>
                    <Input data-testid="mailbox-exclude" value={mbExclude}
                      onChange={(e) => setMbExclude(e.target.value)} placeholder="europacific.com, cargo-cfs.com" />
                    <p className="text-[0.7rem] text-muted-foreground mt-1.5">Comma-separated. Recipients on these domains are skipped.</p>
                  </div>
                  {mbBusy && (
                    <div className="rounded-sm border border-border bg-muted/30 px-3 py-3 text-xs text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading the mailbox's sent items and extracting contacts…
                    </div>
                  )}
                  {mbError && (
                    <div className="rounded-sm border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="mailbox-error">{mbError}</div>
                  )}
                  {mbResult && (
                    <div className="rounded-sm border border-signal-500/30 bg-signal-500/5 px-3 py-2.5 text-xs space-y-1" data-testid="mailbox-result">
                      <div><strong>{mbResult.created}</strong> prospects added from {mbResult.mailbox}</div>
                      <div className="text-muted-foreground">
                        {mbResult.scanned_messages} sent messages scanned · {mbResult.unique_recipients} unique recipients ·
                        {" "}{mbResult.excluded_own_domain} internal skipped · {mbResult.duplicates} already existed
                      </div>
                      {mbResult.sample?.length ? (
                        <div className="pt-1 text-muted-foreground truncate">e.g. {mbResult.sample.slice(0, 6).join(", ")}</div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}

              {addMode === "rfq" && (
                <div className="space-y-3" data-testid="add-rfq-section">
                  <p className="text-[0.75rem] text-muted-foreground">
                    Upload an RFQ you received by mistake (or paste it). The AI pulls out the <strong>end customer</strong>
                    {" "}(shipper/consignee) — <strong>not</strong> the forwarder who sent it — so you can win your competitor's customer directly.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input ref={rfqFileRef} type="file" accept=".msg,.eml,.txt" onChange={onRfqFile} className="hidden" data-testid="rfq-file-input" />
                    <Button variant="outline" size="sm" className="rounded-sm" disabled={rfqBusy}
                      onClick={() => rfqFileRef.current?.click()} data-testid="rfq-upload-btn">
                      {rfqBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                      {rfqBusy ? "Reading RFQ…" : "Upload RFQ file (.msg / .eml)"}
                    </Button>
                    {rfqFileName && <span className="text-[0.7rem] text-muted-foreground inline-flex items-center gap-1"><FileText className="w-3 h-3" />{rfqFileName}</span>}
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1.5">…or paste the RFQ email</label>
                    <Textarea data-testid="rfq-paste-input" rows={4} value={rfqText}
                      onChange={(e) => setRfqText(e.target.value)}
                      placeholder="Paste the forwarded RFQ email here (subject + body)…" />
                    <div className="flex justify-end mt-2">
                      <Button size="sm" className="rounded-sm" disabled={rfqBusy || !rfqText.trim()}
                        onClick={() => parseRfq()} data-testid="rfq-parse-btn">
                        {rfqBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                        {rfqBusy ? "Parsing…" : "Parse RFQ"}
                      </Button>
                    </div>
                  </div>

                  {rfqDraft && (
                    <div className="space-y-3 rounded-sm border border-signal-500/30 bg-signal-500/5 p-3" data-testid="rfq-result">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-[0.6rem] tracking-widest uppercase text-signal-500 font-semibold">Extracted target</span>
                        {rfqDraft.sender_company && (
                          <span className="text-[0.65rem] text-muted-foreground">· RFQ sent by <strong>{rfqDraft.sender_company}</strong> (ignored)</span>
                        )}
                      </div>
                      {rfqDraft.reasoning && <p className="text-[0.7rem] text-muted-foreground italic">{rfqDraft.reasoning}</p>}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="label-eyebrow block mb-1.5">Target company <span className="text-destructive">*</span></label>
                          <Input data-testid="rfq-company" value={rfqDraft.company} onChange={(e) => setRfq("company", e.target.value)} placeholder="e.g. Skupina Posavje d.o.o." />
                        </div>
                        <div>
                          <label className="label-eyebrow block mb-1.5">Contact name <span className="text-muted-foreground">(optional)</span></label>
                          <Input data-testid="rfq-contact-name" value={rfqDraft.contact_name} onChange={(e) => setRfq("contact_name", e.target.value)} placeholder="—" />
                        </div>
                        <div>
                          <label className="label-eyebrow block mb-1.5">Contact email <span className="text-muted-foreground">(optional)</span></label>
                          <Input data-testid="rfq-contact-email" value={rfqDraft.contact_email} onChange={(e) => setRfq("contact_email", e.target.value)} placeholder="—" />
                        </div>
                        <div className="col-span-2">
                          <label className="label-eyebrow block mb-1.5">Location <span className="text-muted-foreground">(optional)</span></label>
                          <Input data-testid="rfq-location" value={rfqDraft.location} onChange={(e) => setRfq("location", e.target.value)} placeholder="e.g. Leskovec pri Krškem, Slovenia" />
                        </div>
                        <div className="col-span-2">
                          <label className="label-eyebrow block mb-1.5">Shipment / RFQ context</label>
                          <Textarea data-testid="rfq-cargo" rows={2} value={rfqDraft.cargo_summary} onChange={(e) => setRfq("cargo_summary", e.target.value)} placeholder="e.g. 4 pallets, SEA→LJU, DAP door-to-door air" />
                        </div>
                      </div>
                      {!rfqDraft.contact_email && (
                        <p className="text-[0.7rem] text-muted-foreground">No customer email in the RFQ — we'll add the company; use <strong>Find real contacts</strong> / <strong>Apollo</strong> to get a decision-maker.</p>
                      )}
                      <p className="text-[0.7rem] text-muted-foreground">Added under the <strong>{addForm.category}</strong> category selected above, flagged as an <strong>active RFQ</strong> (hot).</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
              <Button variant="ghost" onClick={resetAdd} disabled={addSaving} data-testid="add-prospect-cancel">Cancel</Button>
              {addMode === "manual" ? (
                <Button onClick={submitAdd} disabled={addSaving} data-testid="add-prospect-save">
                  {addSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <UserPlus className="w-3.5 h-3.5 mr-1.5" />}
                  {addSaving ? "Adding…" : "Add prospects"}
                </Button>
              ) : addMode === "url" ? (
                <Button onClick={submitBulk} disabled={addSaving || !parsed?.count} data-testid="add-url-save">
                  {addSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <UserPlus className="w-3.5 h-3.5 mr-1.5" />}
                  {addSaving ? "Adding…" : parsed?.count ? `Add ${parsed.count} prospects` : "Add prospects"}
                </Button>
              ) : addMode === "file" ? (
                <Button onClick={submitFileImport} disabled={addSaving || (!filePreview?.with_email && !(includeNoEmail && filePreview?.no_email_companies))} data-testid="add-file-save">
                  {addSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <UserPlus className="w-3.5 h-3.5 mr-1.5" />}
                  {addSaving ? "Importing…" : filePreview?.with_email ? `Import ${filePreview.with_email} prospects` : "Import prospects"}
                </Button>
              ) : addMode === "mailbox" ? (
                <Button onClick={runMailboxParse} disabled={mbBusy || !mbAddress.trim()} data-testid="mailbox-scan-btn">
                  {mbBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                  {mbBusy ? "Scanning…" : "Scan & add prospects"}
                </Button>
              ) : (
                <Button onClick={submitRfq} disabled={addSaving || !rfqDraft?.company?.trim()} data-testid="add-rfq-save">
                  {addSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <UserPlus className="w-3.5 h-3.5 mr-1.5" />}
                  {addSaving ? "Adding…" : "Add target to prospects"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function ReactivateByLane({ onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [enrolling, setEnrolling] = useState(null);

  const fetchLanes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reactivation/lanes");
      setData(data);
    } catch (e) {
      toast.error("Could not load lanes", { description: e?.response?.data?.detail || "" });
    }
    setLoading(false);
  };

  useEffect(() => { fetchLanes(); }, []);

  const enroll = async (lane) => {
    setEnrolling(lane.id);
    try {
      const { data: res } = await api.post(`/reactivation/lanes/${lane.id}/enroll`);
      toast.success(`Reactivated ${res.enrolled} agent(s)`, {
        description: `${lane.label} — warm re-connect emails will go out via the sequencer.`,
      });
      await fetchLanes();
      onDone && onDone();
    } catch (e) {
      toast.error("Reactivation failed", { description: e?.response?.data?.detail || "" });
    }
    setEnrolling(null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="reactivate-modal">
      <div className="bg-card border border-border rounded-sm shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-signal-500" />
            <div>
              <div className="label-eyebrow">Reactivate dormant agents</div>
              <div className="font-display font-semibold">By trade lane</div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="reactivate-close"><X className="w-4 h-4" /></Button>
        </div>
        {loading ? (
          <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Finding dormant agents…
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <p className="text-xs text-muted-foreground">
              Known network-partner contacts (e.g. from your Evernote cards) that are emailable but idle,
              grouped by origin region. Enrolling fires a warm re-connect email cadence.
              <span className="font-mono"> {data?.total_dormant || 0} dormant · {data?.unmatched || 0} unmatched region.</span>
            </p>
            {(data?.lanes || []).map((lane) => (
              <div key={lane.id} className="flex items-center justify-between gap-3 border border-border rounded-sm p-3"
                data-testid={`reactivate-lane-${lane.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm">{lane.label}</div>
                  <div className="text-xs text-muted-foreground truncate">{lane.blurb}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono text-lg font-semibold text-signal-500" data-testid={`reactivate-count-${lane.id}`}>{lane.count}</span>
                  <Button size="sm" disabled={!lane.count || enrolling === lane.id}
                    onClick={() => enroll(lane)} data-testid={`reactivate-enroll-${lane.id}`}
                    className="bg-signal-500 hover:bg-signal-500/90 text-white disabled:bg-muted disabled:text-muted-foreground disabled:opacity-60">
                    {enrolling === lane.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Reactivate"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
